import { useEffect, useRef, useState } from 'react';

const industries = [
  {
    icon: 'ri-building-4-line',
    title: 'Professional Services',
    subtitle: 'Law, Accounting, Consulting, Finance',
    description: 'Automate intake, follow-ups, document workflows, and client communication for firms that need operational precision.',
    features: [
      'Automated client onboarding',
      'Document request workflows',
      'Follow-up email sequences',
      'Meeting scheduling & reminders',
    ],
    image: 'https://public.readdy.ai/ai/img_res/d39ed6da31ba9c36b0e615cf8fac4e9d.jpg',
    accent: '#7C3AED',
  },
  {
    icon: 'ri-tools-fill',
    title: 'Home Services & Trades',
    subtitle: 'HVAC, Plumbing, Electrical, Landscaping',
    description: 'Capture more leads, streamline dispatch and scheduling, and reduce missed opportunities in fast-moving service environments.',
    features: [
      'Lead capture & routing',
      'Automated dispatch notifications',
      'Job scheduling automation',
      'Review request workflows',
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20home%20services%20technician%20modern%20van%20equipment%20organized%20tools%20clean%20exterior%20suburban%20bright%20sunny%20day%20minimal%20background%20no%20people&width=800&height=500&seq=wwai-industry-hs01&orientation=landscape',
    accent: '#14B8A6',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Healthcare & Medical',
    subtitle: 'Clinics, Therapists, Specialists, Wellness',
    description: 'Improve patient intake, appointment flow, reminders, and administrative efficiency with connected systems.',
    features: [
      'Patient intake automation',
      'Appointment reminders & rescheduling',
      'Insurance & document workflows',
      'Post-visit follow-up sequences',
    ],
    image: 'https://readdy.ai/api/search-image?query=modern%20healthcare%20medical%20clinic%20interior%20clean%20white%20minimal%20professional%20reception%20desk%20bright%20lighting%20organized%20contemporary%20medical%20office%20design%20no%20people&width=800&height=500&seq=wwai-industry-hc01&orientation=landscape',
    accent: '#7C3AED',
  },
];

const IndustriesSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const active = industries[activeIdx];

  return (
    <section id="industries" className="py-28 bg-white overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className="text-center max-w-xl mx-auto mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="font-inter text-xs font-semibold text-brand-teal tracking-widest uppercase mb-4 block">
            Industries
          </span>
          <h2
            className="font-space font-semibold text-brand-text leading-tight mb-5"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)' }}
          >
            Built for{' '}
            <span className="text-brand-violet">Service-Based Businesses</span>
          </h2>
          <p className="font-inter text-brand-steel text-base leading-relaxed">
            We specialize in service businesses where operational efficiency directly impacts revenue, client satisfaction, and growth capacity.
          </p>
        </div>

        {/* Tab selector */}
        <div
          className="flex flex-wrap justify-center gap-3 mb-10"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.2s',
          }}
        >
          {industries.map((ind, i) => (
            <button
              key={ind.title}
              onClick={() => setActiveIdx(i)}
              className="whitespace-nowrap font-inter text-sm font-medium px-5 py-2.5 rounded-lg cursor-pointer transition-all duration-200 flex items-center gap-2"
              style={{
                background: activeIdx === i ? `${ind.accent}15` : 'transparent',
                color: activeIdx === i ? ind.accent : '#64748B',
                border: `1px solid ${activeIdx === i ? `${ind.accent}40` : '#E2E8F0'}`,
              }}
            >
              <i className={`${ind.icon} text-base`} />
              {ind.title}
            </button>
          ))}
        </div>

        {/* Active industry card */}
        <div
          className="grid lg:grid-cols-2 gap-10 items-start rounded-2xl overflow-hidden border border-brand-light"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s',
          }}
        >
          {/* Left: image */}
          <div className="relative h-80 lg:h-full min-h-72 overflow-hidden">
            <img
              src={active.image}
              alt={active.title}
              className="w-full h-full object-cover object-top transition-all duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10" />
          </div>

          {/* Right: content */}
          <div className="p-8 lg:p-10 flex flex-col justify-center">
            {/* Icon + title */}
            <div className="flex items-center gap-3 mb-2">
              <div
                className="w-10 h-10 flex items-center justify-center rounded-lg"
                style={{ background: `${active.accent}12` }}
              >
                <i className={`${active.icon} text-lg`} style={{ color: active.accent }} />
              </div>
              <div>
                <h3 className="font-space font-semibold text-brand-text text-lg">{active.title}</h3>
                <p className="font-inter text-brand-steel text-xs">{active.subtitle}</p>
              </div>
            </div>

            <div className="w-12 h-0.5 rounded-full my-5" style={{ background: active.accent }} />

            <p className="font-inter text-brand-steel text-sm leading-relaxed mb-6">
              {active.description}
            </p>

            {/* Features */}
            <div className="flex flex-col gap-3 mb-8">
              {active.features.map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <div
                    className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ background: `${active.accent}15` }}
                  >
                    <i className="ri-check-line text-xs" style={{ color: active.accent }} />
                  </div>
                  <span className="font-inter text-brand-text text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => document.querySelector('#hero')?.scrollIntoView({ behavior: 'smooth' })}
              className="whitespace-nowrap font-inter font-semibold text-sm text-white px-6 py-3 rounded-lg cursor-pointer transition-all duration-200 hover:opacity-90 self-start flex items-center gap-2"
              style={{ background: `linear-gradient(135deg, ${active.accent}, ${active.accent}cc)` }}
            >
              Get a Custom Automation Plan
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
